import { useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowRight, ChevronDown, RotateCw } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { WebsiteBlocks } from "@/components/WebsiteBlocks";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import FractionalInquiryDrawer from "@/components/FractionalInquiryDrawer";
import { Link } from "wouter";
import type { WebsiteBlock, WebsiteDocument, WebsiteMediaView } from "@shared/websiteContent";
import { FRACTIONAL_SERVICES_CONTENT } from "@shared/fractionalServices";
import { FRACTIONAL_SERVICE_IMAGES } from "@shared/fractionalServicesMedia";

type FractionalServicesContentProps = {
  content: WebsiteDocument;
  media: WebsiteMediaView[];
  allowInquiry?: boolean;
};

const serviceIds = ["talent-acquisition", "employee-experience", "compensation"] as const;
const processIds = ["discover", "assess", "design", "measure"] as const;

function blockIndex(blocks: WebsiteBlock[], id: string) {
  return blocks.findIndex((block) => block.id === id);
}

function nextIndex(blocks: WebsiteBlock[], start: number, ids: readonly string[]) {
  const candidates = ids
    .map((id) => blockIndex(blocks, id))
    .filter((index) => index > start);
  return candidates.length ? Math.min(...candidates) : blocks.length;
}

function BlockContent({ blocks, media }: { blocks: WebsiteBlock[]; media: WebsiteMediaView[] }) {
  if (!blocks.length) return null;
  return (
    <div className="fractional-blocks">
      <WebsiteBlocks blocks={blocks} media={media} />
    </div>
  );
}

function sectionBlocks(blocks: WebsiteBlock[], id: string, stops: readonly string[]) {
  const start = blockIndex(blocks, id);
  if (start < 0) return [];
  return blocks.slice(start, nextIndex(blocks, start, stops));
}

function ServiceCards({ blocks, media }: { blocks: WebsiteBlock[]; media: WebsiteMediaView[] }) {
  const [openDetailsId, setOpenDetailsId] = useState<string | null>(null);
  const headingIndex = blockIndex(blocks, "services");
  if (headingIndex < 0) return null;
  const serviceHeading = blocks[headingIndex];
  const stop = nextIndex(blocks, headingIndex, ["how-we-work", "delivery", "get-started"]);
  const group = blocks.slice(headingIndex + 1, stop);
  const cardStarts = serviceIds
    .map((id) => ({ id, index: group.findIndex((block) => block.id === id) }))
    .filter((item) => item.index >= 0);
  const prefix = group.slice(0, cardStarts[0]?.index ?? group.length);

  return (
    <section className="fractional-section fractional-services" aria-labelledby="services">
      <div className="fractional-section-heading">
        <span className="fractional-index">01 / EXPERTISE</span>
        <div>
          <BlockContent blocks={[serviceHeading]} media={media} />
          <BlockContent blocks={prefix} media={media} />
        </div>
      </div>
      {cardStarts.length > 0 ? (
        <div className="fractional-service-list">
          {cardStarts.map(({ id, index }, cardIndex) => {
            const end = cardStarts[cardIndex + 1]?.index ?? group.length;
            const cardBlocks = group.slice(index, end);
            const prefix = id === "talent-acquisition" ? "talent" : id === "employee-experience" ? "employee" : "compensation";
            const visibleIds = new Set([id, `${prefix}-motto`, `${prefix}-intro`]);
            const visibleBlocks = cardBlocks.filter((block) => visibleIds.has(block.id));
            const detailBlocks = cardBlocks.filter((block) => !visibleIds.has(block.id));
            const image = FRACTIONAL_SERVICE_IMAGES[id];
            const selectedImageBlock = cardBlocks.find(
              (block) => block.type === "image" && Boolean(block.mediaId),
            );
            const selectedImage = selectedImageBlock
              ? media.find((item) => item.id === selectedImageBlock.mediaId)
              : undefined;
            return (
              <article className="fractional-service" key={id}>
                <div className="fractional-service__number" aria-hidden="true">
                  0{cardIndex + 1}
                </div>
                <div className="fractional-service__visual">
                  <img src={selectedImage?.url ?? image.url} alt={selectedImage?.alt || image.alt} loading="lazy" />
                  <span>CHI / {String(cardIndex + 1).padStart(2, "0")}</span>
                </div>
                <div className="fractional-service__content">
                  <BlockContent blocks={visibleBlocks} media={media} />
                  {detailBlocks.length > 0 && (
                    <Collapsible
                      className="fractional-service__details"
                      open={openDetailsId === id}
                      onOpenChange={(open) => setOpenDetailsId(open ? id : null)}
                    >
                      <CollapsibleTrigger className="fractional-service__details-trigger">
                        <span>Explore all capabilities</span>
                        <ChevronDown className="fractional-service__details-chevron" size={17} aria-hidden="true" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="fractional-service__details-content">
                        <div className="fractional-service__details-copy">
                          <BlockContent blocks={detailBlocks} media={media} />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <BlockContent blocks={group} media={media} />
      )}
    </section>
  );
}

function ProcessSteps({ blocks, media }: { blocks: WebsiteBlock[]; media: WebsiteMediaView[] }) {
  const deliveryIndex = blockIndex(blocks, "delivery");
  if (deliveryIndex < 0) return null;
  const end = nextIndex(blocks, deliveryIndex, ["get-started"]);
  const body = blocks.slice(deliveryIndex, end);
  const stepStarts = processIds
    .map((id) => ({ id, index: body.findIndex((block) => block.id === id) }))
    .filter((item) => item.index >= 0);
  const beforeSteps = body.slice(0, stepStarts[0]?.index ?? body.length);

  return (
    <section className="fractional-section fractional-delivery">
      <div className="fractional-delivery__intro">
        <span className="fractional-index">03 / DELIVERY</span>
        <BlockContent blocks={beforeSteps} media={media} />
      </div>
      {stepStarts.length > 0 ? (
        <div className="fractional-steps">
          {stepStarts.map(({ id, index }, stepIndex) => (
            <article className="fractional-step" key={id}>
              <span className="fractional-step__number">0{stepIndex + 1}</span>
              <BlockContent blocks={body.slice(index, stepStarts[stepIndex + 1]?.index ?? body.length)} media={media} />
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function Extras({ blocks, media, omittedIds }: { blocks: WebsiteBlock[]; media: WebsiteMediaView[]; omittedIds: Set<string> }) {
  const extras = blocks.filter((block) => !omittedIds.has(block.id));
  return extras.length ? <BlockContent blocks={extras} media={media} /> : null;
}

export function FractionalServicesContent({
  content,
  media,
  allowInquiry = true,
}: FractionalServicesContentProps) {
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const inquiryTriggerRef = useRef<HTMLButtonElement>(null);
  const blocks = useMemo(() => content.blocks ?? [], [content.blocks]);
  const heroEnd = blockIndex(blocks, "services");
  const heroBlocks = heroEnd < 0 ? blocks : blocks.slice(0, heroEnd);
  const serviceStop = heroEnd < 0 ? blocks.length : nextIndex(blocks, heroEnd, ["how-we-work", "delivery", "get-started"]);
  const serviceBlocks = heroEnd < 0 ? [] : blocks.slice(heroEnd, serviceStop);
  const howWork = sectionBlocks(blocks, "how-we-work", ["delivery", "get-started"]);
  const deliveryBlocks = sectionBlocks(blocks, "delivery", ["get-started"]);
  const cta = sectionBlocks(blocks, "get-started", []);
  const consumed = useMemo(
    () =>
      new Set([
        ...heroBlocks.map((block) => block.id),
        ...serviceBlocks.map((block) => block.id),
        ...howWork.map((block) => block.id),
        ...deliveryBlocks.map((block) => block.id),
        ...cta.map((block) => block.id),
      ]),
    [blocks, cta, deliveryBlocks, heroBlocks, howWork, serviceBlocks],
  );

  return (
    <main className="fractional-shell fractional-page">
      <style>{styles}</style>
      <div className="fractional-hero">
        <div className="fractional-hero__wash" aria-hidden="true" />
        <div className="fractional-hero__inner">
          <div className="fractional-hero__copy">
            <p className="fractional-eyebrow"><span /> THE CHI ALLIANCE <i>/</i> FRACTIONAL SERVICES</p>
            <h1>{content.title}</h1>
            {content.description && <p className="fractional-hero__summary">{content.description}</p>}
            <BlockContent blocks={heroBlocks.filter(block => block.id !== "explore-services")} media={media} />
            {heroBlocks.find(block => block.id === "explore-services" && block.href) && (
              <a className="fractional-primary-link" href={heroBlocks.find(block => block.id === "explore-services")!.href!.replace(/^\/fractional-services(?=[#?]|$)/, "/hr-services")}>
                {heroBlocks.find(block => block.id === "explore-services")!.text} <ArrowDown size={16} aria-hidden="true" />
              </a>
            )}
          </div>
        </div>
        <div className="fractional-hero__bottomline">
          <span>PEOPLE. PURPOSE. PROGRESS.</span>
          <span>01 — 04</span>
        </div>
      </div>

      <div className="fractional-content">
        <ServiceCards blocks={blocks} media={media} />

        {howWork.length > 0 && (
          <section className="fractional-section fractional-how">
            <div className="fractional-how__visual" role="img" aria-label="A welcoming space for conversation and collaboration">
              <span className="fractional-how__visual-label">A PARTNERSHIP, NOT A PLAYBOOK</span>
            </div>
            <div className="fractional-how__copy">
              <span className="fractional-index">02 / HOW WE WORK</span>
              <BlockContent blocks={howWork} media={media} />
            </div>
          </section>
        )}

        <ProcessSteps blocks={blocks} media={media} />

        {cta.length > 0 && (
          <section className="fractional-contact" id="start-conversation">
            <img
              className="fractional-contact__image"
              src={FRACTIONAL_SERVICE_IMAGES.conversation.url}
              alt={FRACTIONAL_SERVICE_IMAGES.conversation.alt}
              loading="eager"
            />
            <div className="fractional-contact__overlay" aria-hidden="true" />
            <div className="fractional-contact__content">
              <span className="fractional-index">A CONVERSATION CAN START HERE</span>
              <BlockContent blocks={cta.filter((block) => block.id !== "contact")} media={media} />
              {(() => {
                const contact = cta.find((block) => block.id === "contact");
                if (!contact) return null;
                if (["info@thechialliance.com", "Start a Conversation"].includes(contact.text?.trim() ?? "")) return null;
                return (
                  <p className="fractional-contact__editable">{contact.text}</p>
                );
              })()}
              <button
                className="fractional-contact__link"
                ref={inquiryTriggerRef}
                type="button"
                onClick={() => allowInquiry && setInquiryOpen(true)}
                disabled={!allowInquiry}
              >
                <span>Start a Conversation</span>
                <ArrowRight size={18} aria-hidden="true" />
              </button>
            </div>
          </section>
        )}

        <Extras blocks={blocks} media={media} omittedIds={consumed} />
      </div>
      {allowInquiry && <FractionalInquiryDrawer open={inquiryOpen} onOpenChange={setInquiryOpen} returnFocusRef={inquiryTriggerRef} />}
    </main>
  );
}

function PageState({ kind, onRetry }: { kind: "loading" | "error" | "missing"; onRetry?: () => void }) {
  return (
    <main className={`fractional-state fractional-state--${kind}`} aria-live="polite">
      <div className="fractional-state__stamp">THE CHI ALLIANCE <span>/</span> FRACTIONAL SERVICES</div>
      {kind === "loading" ? (
        <>
          <div className="fractional-state__skeleton fractional-state__skeleton--title" />
          <div className="fractional-state__skeleton" />
          <div className="fractional-state__skeleton fractional-state__skeleton--short" />
          <p>Loading the page…</p>
        </>
      ) : kind === "error" ? (
        <>
          <span className="fractional-state__eyebrow">A MOMENT, PLEASE</span>
          <h1>This page is temporarily unavailable.</h1>
          <p>We couldn’t load the fractional services page. Please try again.</p>
          <button className="fractional-state__button" type="button" onClick={onRetry}>
            <RotateCw size={16} aria-hidden="true" /> Try again
          </button>
        </>
      ) : (
        <>
          <span className="fractional-state__eyebrow">NOT PUBLISHED</span>
          <h1>This page isn’t available yet.</h1>
          <p>The fractional services page may have moved or is not published yet.</p>
          <Link href="/" className="fractional-state__home">Return to The CHI Alliance <ArrowRight size={16} aria-hidden="true" /></Link>
        </>
      )}
    </main>
  );
}

export default function FractionalServices() {
  return (
    <div className="fractional-shell">
      <Navigation />
      <FractionalServicesContent content={FRACTIONAL_SERVICES_CONTENT} media={[]} />
      <Footer />
      <style>{styles}</style>
    </div>
  );
}

const styles = `
.fractional-shell{--ink:#201d31;--muted:#5f5b70;--paper:#f5f1eb;--paper-deep:#ebe4dc;--plum:#57264e;--rose:#bd4c83;--gold:#e3aa59;--line:rgba(42,32,55,.17);min-height:100dvh;background:var(--paper);color:var(--ink);font-family:"Space Grotesk",sans-serif}
.fractional-page{overflow:hidden;padding-top:72px}
.fractional-hero{position:relative;min-height:670px;background:#2b1e39;color:#fbf5ef;isolation:isolate}
.fractional-hero:before{position:absolute;inset:0;z-index:-2;content:"";background:linear-gradient(112deg,rgba(31,20,43,.98) 0%,rgba(65,31,66,.94) 54%,rgba(31,23,43,.84) 100%)}
.fractional-hero:after{position:absolute;inset:0;z-index:-1;content:"";opacity:.16;pointer-events:none;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.72' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.25'/%3E%3C/svg%3E")}
.fractional-hero__wash{position:absolute;z-index:-1;inset:0;background:radial-gradient(ellipse at 70% 25%,rgba(192,93,140,.24),transparent 45%),linear-gradient(90deg,rgba(33,22,43,.9),transparent 70%)}
.fractional-hero__inner{position:relative;display:grid;grid-template-columns:minmax(0,1.05fr) minmax(280px,.82fr);align-items:center;gap:clamp(44px,8vw,120px);width:min(1250px,calc(100% - 64px));min-height:640px;margin:auto;padding:80px 0 94px}
.fractional-hero__copy{position:relative;z-index:2;max-width:670px;animation:frac-rise .65s both}
.fractional-eyebrow,.fractional-index{display:flex;align-items:center;gap:10px;color:#d9b6c8;font-size:.68rem;font-weight:700;letter-spacing:.16em;text-transform:uppercase}
.fractional-eyebrow span{width:25px;height:1px;background:var(--gold)}
.fractional-eyebrow i{font-style:normal;color:#bd83a7}
.fractional-hero h1{max-width:700px;margin:31px 0 23px;color:#fbf5ef;font-family:"DM Serif Display",Georgia,serif;font-size:clamp(3.2rem,7vw,6.5rem);font-weight:400;letter-spacing:-.045em;line-height:.98}
.fractional-hero__summary{max-width:590px;margin:0 0 16px;color:#eedfe7;font-size:clamp(1.1rem,1.8vw,1.42rem);line-height:1.62}
.fractional-hero .fractional-blocks{max-width:600px;color:#d4c5d0;font-size:1rem}
.fractional-hero .fractional-blocks h2{color:#fff7f0;font-size:clamp(1.45rem,2vw,1.85rem)}
.fractional-hero .fractional-blocks p,.fractional-hero .fractional-blocks li{color:#e8dfe7;margin:0 0 .65rem;line-height:1.75}
.fractional-primary-link{display:inline-flex;align-items:center;gap:13px;min-height:50px;margin-top:25px;padding:0 20px;border:1px solid rgba(240,212,225,.44);border-radius:2px;color:#fff4eb;text-decoration:none;font-size:.84rem;font-weight:700;letter-spacing:.025em;transition:transform .2s ease,background-color .2s ease}
.fractional-primary-link:hover{transform:translateY(-2px);background:rgba(255,255,255,.09)}
.fractional-primary-link svg{color:#e9b65e}
.fractional-hero__image{position:relative;align-self:stretch;min-height:410px;margin:0 30px 0 0;background:url("/media/backgrounds/community-networking.webp") center/cover no-repeat;box-shadow:14px 17px 0 rgba(227,170,89,.78);animation:frac-rise .8s .12s both}
.fractional-hero__image:before{position:absolute;inset:0;content:"";border:1px solid rgba(255,255,255,.38);pointer-events:none}
.fractional-hero__image-shade{position:absolute;inset:0;background:linear-gradient(0deg,rgba(34,19,38,.64),transparent 54%),linear-gradient(90deg,rgba(55,25,49,.16),transparent)}
.fractional-hero__caption{position:absolute;bottom:24px;left:25px;right:25px;display:flex;align-items:center;gap:12px;color:#fff;font-size:.65rem;font-weight:700;letter-spacing:.16em}
.fractional-hero__rule{height:1px;flex:1;background:rgba(255,255,255,.68)}
.fractional-hero__side-note{position:absolute;right:0;top:50%;color:rgba(255,245,239,.63);font-size:.62rem;font-weight:700;letter-spacing:.21em;writing-mode:vertical-rl;transform:translateY(-50%)}
.fractional-hero__bottomline{position:absolute;left:max(32px,calc((100vw - 1250px)/2));right:max(32px,calc((100vw - 1250px)/2));bottom:0;display:flex;justify-content:space-between;padding:17px 0;border-top:1px solid rgba(255,255,255,.22);color:#c8b5c4;font-size:.62rem;font-weight:700;letter-spacing:.17em}
.fractional-content{width:min(1250px,calc(100% - 64px));margin:0 auto;padding:112px 0 92px}
.fractional-section{scroll-margin-top:100px}
.fractional-services{padding:0 0 116px}
.fractional-section-heading{display:grid;grid-template-columns:190px 1fr;gap:24px;align-items:start;margin-bottom:35px}
.fractional-index{color:#98607d}
.fractional-section-heading>.fractional-index{padding-top:10px}
.fractional-blocks h2,.fractional-blocks h3{margin:0;color:var(--ink);font-family:"DM Serif Display",Georgia,serif;font-weight:400;letter-spacing:-.025em;line-height:1.1}
.fractional-section-heading .fractional-blocks h2{font-size:clamp(2.5rem,5vw,4.25rem)}
.fractional-section-heading .fractional-blocks p{max-width:720px;color:var(--muted);font-size:1rem;line-height:1.75}
.fractional-blocks p{margin:0 0 1rem;white-space:pre-line;line-height:1.75}
.fractional-blocks p:last-child{margin-bottom:0}
.fractional-blocks a{color:var(--plum);font-weight:700}
.fractional-blocks figure{margin:1.5rem 0}
.fractional-blocks figure img,.fractional-blocks figure video{display:block;width:100%;max-height:520px;object-fit:cover}
.fractional-blocks figcaption{padding-top:9px;color:var(--muted);font-size:.82rem}
.fractional-blocks .website-content-cta{display:inline-flex;min-height:48px;align-items:center;padding:0 18px;border-radius:2px;background:var(--plum);color:#fff;text-decoration:none}
.fractional-service-list{border-top:1px solid var(--line)}
.fractional-service{position:relative;display:grid;grid-template-columns:72px 1fr 32px;gap:25px;align-items:start;padding:34px 10px 36px 0;border-bottom:1px solid var(--line);transition:padding .22s ease}
.fractional-service:hover{padding-left:10px}
.fractional-service__number{padding-top:3px;color:#a05e7e;font-family:"DM Serif Display",Georgia,serif;font-size:1.4rem}
.fractional-service__content{max-width:850px}
.fractional-service .fractional-blocks h2{margin:0 0 12px;font-size:clamp(1.65rem,3vw,2.5rem)}
.fractional-service .fractional-blocks h3{margin:0 0 13px;color:var(--rose);font-family:"Space Grotesk",sans-serif;font-size:.69rem;font-weight:700;letter-spacing:.16em;text-transform:uppercase}
.fractional-service .fractional-blocks p{max-width:850px;color:#5c5769;font-size:.98rem}
.fractional-how{display:grid;grid-template-columns:minmax(260px,.92fr) minmax(0,1.08fr);gap:clamp(38px,8vw,110px);align-items:center;padding:83px 0 113px;border-top:1px solid var(--line)}
.fractional-how__visual{position:relative;min-height:430px;background:url("/media/backgrounds/mission-human-connection.webp") center/cover no-repeat}
.fractional-how__visual:after{position:absolute;inset:0;content:"";background:linear-gradient(0deg,rgba(42,24,49,.55),transparent 60%)}
.fractional-how__visual-label{position:absolute;z-index:1;left:20px;bottom:20px;color:white;font-size:.63rem;font-weight:700;letter-spacing:.15em}
.fractional-how__copy>.fractional-index{margin-bottom:24px}
.fractional-how__copy .fractional-blocks h2{margin:0 0 19px;font-size:clamp(2.5rem,5vw,4.1rem)}
.fractional-how__copy .fractional-blocks p{margin:0 0 17px;color:var(--muted);font-size:1rem}
.fractional-delivery{position:relative;margin:0 calc(50% - 50vw);padding:89px max(32px,calc((100vw - 1250px)/2)) 102px;background:var(--paper-deep)}
.fractional-delivery__intro{display:grid;grid-template-columns:190px 1fr;gap:24px;max-width:1080px;margin:0 auto 48px}
.fractional-delivery__intro>.fractional-index{padding-top:9px}
.fractional-delivery__intro .fractional-blocks h2{margin-bottom:10px;font-size:clamp(2.5rem,5vw,4.1rem)}
.fractional-delivery__intro .fractional-blocks h3{margin-bottom:15px;color:var(--rose);font-family:"Space Grotesk",sans-serif;font-size:.68rem;letter-spacing:.15em;text-transform:uppercase}
.fractional-delivery__intro .fractional-blocks p{max-width:760px;color:var(--muted)}
.fractional-steps{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));max-width:1250px;margin:0 auto;border-top:1px solid rgba(48,33,58,.2)}
.fractional-step{position:relative;padding:22px 24px 5px 0}
.fractional-step:not(:last-child):after{position:absolute;top:-4px;right:22px;width:7px;height:7px;border-radius:50%;background:var(--rose);content:""}
.fractional-step__number{display:block;margin-bottom:21px;color:#a26b83;font-family:"DM Serif Display",Georgia,serif;font-size:1.6rem}
.fractional-step .fractional-blocks h2{margin:0 0 12px;font-size:1.6rem}
.fractional-step .fractional-blocks h3{margin:0 0 10px;color:var(--plum);font-family:"Space Grotesk",sans-serif;font-size:.68rem;letter-spacing:.12em;text-transform:uppercase}
.fractional-step .fractional-blocks p{color:#625a68;font-size:.91rem;line-height:1.72}
.fractional-contact{position:relative;display:grid;grid-template-columns:190px 1fr;gap:24px;margin-top:105px;padding:56px clamp(24px,6vw,74px);overflow:hidden;background:#37233f;color:#fff6f0}
.fractional-contact:after{position:absolute;right:-60px;bottom:-165px;width:430px;height:430px;border:1px solid rgba(238,186,202,.24);border-radius:50%;box-shadow:0 0 0 35px rgba(238,186,202,.04),0 0 0 70px rgba(238,186,202,.035);content:"";pointer-events:none}
.fractional-contact__mark{align-self:start;color:rgba(255,255,255,.17);font-family:"DM Serif Display",Georgia,serif;font-size:clamp(3.5rem,7vw,6rem);line-height:1}
.fractional-contact__content{position:relative;z-index:1}
.fractional-contact__content>.fractional-index{margin-bottom:20px;color:#e2aec4}
.fractional-contact .fractional-blocks h2{max-width:790px;margin:0 0 17px;color:#fff7f0;font-size:clamp(2.4rem,5vw,4.5rem)}
.fractional-contact .fractional-blocks h3{margin:0 0 12px;color:#e9b65e;font-size:.75rem;letter-spacing:.14em;text-transform:uppercase}
.fractional-contact .fractional-blocks p{max-width:740px;color:#dfd2df}
.fractional-contact .fractional-blocks p:last-child{margin-bottom:0}
.fractional-contact .fractional-blocks figure{max-width:700px}
.fractional-contact__link{position:relative;display:inline-flex;min-height:50px;align-items:center;gap:13px;margin-top:26px;padding:0 18px;border:1px solid rgba(255,255,255,.36);color:#fff;text-decoration:none;font-size:.93rem;font-weight:700}
.fractional-contact__link svg:first-child{color:#f0bb67}
.fractional-contact__link:hover{background:rgba(255,255,255,.09)}
.fractional-shell :is(.fractional-hero,.fractional-contact) :is(h1,h2,h3,h4,p,li,a,figcaption,.website-blocks,.fractional-eyebrow,.fractional-eyebrow i,.fractional-index,.fractional-hero__caption,.fractional-hero__side-note,.fractional-hero__bottomline){color:#fff}
.fractional-shell .fractional-how__visual-label{color:#fff}
.fractional-shell footer :is(h2,h3,p,a,span){color:#fff!important}
.fractional-state{display:grid;min-height:70dvh;align-content:center;justify-items:center;gap:15px;padding:120px 24px 80px;background:var(--paper,#f5f1eb);color:#272134;text-align:center}
.fractional-state__stamp{margin-bottom:25px;color:#9b6681;font-size:.65rem;font-weight:700;letter-spacing:.16em}
.fractional-state__stamp span{padding:0 7px;color:#d1a75f}
.fractional-state__eyebrow{color:#9b6681;font-size:.68rem;font-weight:700;letter-spacing:.16em}
.fractional-state h1{max-width:660px;margin:0;font-family:"DM Serif Display",Georgia,serif;font-size:clamp(2.2rem,5vw,3.8rem);font-weight:400;line-height:1.1}
.fractional-state p{max-width:540px;margin:0;color:#625b6d;line-height:1.7}
.fractional-state__button,.fractional-state__home{display:inline-flex;min-height:48px;align-items:center;justify-content:center;gap:10px;margin-top:12px;padding:0 18px;border:1px solid #6e405f;background:#57264e;color:white;font:700 .9rem "Space Grotesk",sans-serif;text-decoration:none;cursor:pointer}
.fractional-state__skeleton{width:min(550px,82vw);height:20px;background:linear-gradient(90deg,#e8dedf 25%,#f0e8e5 50%,#e8dedf 75%);background-size:200% 100%;animation:frac-shimmer 1.6s linear infinite}
.fractional-state__skeleton--title{width:min(460px,78vw);height:46px;margin-bottom:7px}
.fractional-state__skeleton--short{width:min(340px,62vw);justify-self:center}
.fractional-state--loading p{font-size:.82rem}
.fractional-hero{min-height:0}
.fractional-hero:before{background:linear-gradient(112deg,#30203d 0%,#57264e 100%)}
.fractional-hero__wash{background:radial-gradient(ellipse at 83% 20%,rgba(211,139,167,.2),transparent 38%),linear-gradient(90deg,rgba(33,22,43,.08),transparent)}
.fractional-hero__inner{display:block;width:min(1250px,calc(100% - 64px));min-height:0;padding:66px 0 75px}
.fractional-hero__copy{width:75%;max-width:100%;text-align:left;animation:none;opacity:1}
.fractional-hero h1{max-width:1020px;margin:22px 0 15px;font-size:clamp(3rem,7.2vw,6.1rem)}
.fractional-hero__summary{max-width:1050px;color:#fff;font-size:clamp(1.08rem,1.8vw,1.34rem)}
.fractional-hero .fractional-blocks{max-width:100%;color:#fff}
.fractional-hero .fractional-blocks h2,.fractional-hero .fractional-blocks h3{color:#fff}
.fractional-hero .fractional-blocks p,.fractional-hero .fractional-blocks li{color:#fff}
.fractional-hero__bottomline{color:#fff}
.fractional-service-list{display:grid;gap:24px;border:0}
.fractional-service{position:relative;grid-template-columns:minmax(0,.88fr) minmax(0,1.12fr);gap:clamp(28px,5vw,68px);align-items:stretch;min-height:370px;margin:0;padding:24px;border:1px solid rgba(73,47,65,.2);border-radius:18px;background:#fbf8f3;box-shadow:0 12px 35px rgba(58,39,51,.055);transition:transform .25s ease,box-shadow .25s ease}
.fractional-service:hover{padding-left:24px;transform:translateY(-3px);box-shadow:0 17px 42px rgba(58,39,51,.1)}
.fractional-service__number{position:absolute;z-index:2;top:37px;left:38px;padding:7px 10px;border:1px solid rgba(255,255,255,.7);border-radius:999px;background:rgba(44,29,47,.72);color:#fff;font-family:"Space Grotesk",sans-serif;font-size:.65rem;font-weight:700;letter-spacing:.12em;line-height:1}
.fractional-service__visual{position:relative;min-height:320px;overflow:hidden;border-radius:12px;background:#ded3cf}
.fractional-service__visual img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transition:transform .5s ease}
.fractional-service:hover .fractional-service__visual img{transform:scale(1.035)}
.fractional-service__visual:after{position:absolute;inset:45% 0 0;content:"";background:linear-gradient(0deg,rgba(40,27,44,.58),transparent)}
.fractional-service__visual span{position:absolute;z-index:1;left:13px;bottom:12px;color:#fff;font-size:.59rem;font-weight:700;letter-spacing:.15em}
.fractional-service__content{max-width:780px;align-self:center;padding:18px 22px 18px 0}
.fractional-service .fractional-blocks h2{font-size:clamp(1.55rem,2.35vw,2.2rem)}
.fractional-service .fractional-blocks h3{margin-bottom:10px;color:#9d5579}
.fractional-service .fractional-blocks p{font-size:.94rem}
.fractional-service__details{margin-top:14px;border-top:1px solid var(--line)}
.fractional-service__details-trigger{display:flex;width:100%;min-height:46px;align-items:center;justify-content:space-between;gap:14px;border:0;background:transparent;padding:0;color:var(--plum);font:700 .78rem "Space Grotesk",sans-serif;letter-spacing:.025em;text-align:left;cursor:pointer}
.fractional-service__details-trigger:focus-visible{outline:2px solid #925b79;outline-offset:3px}
.fractional-service__details-chevron{flex:none;transition:transform .22s ease}
.fractional-service__details[data-state="open"] .fractional-service__details-chevron{transform:rotate(180deg)}
.fractional-service__details-content{overflow:hidden}
.fractional-service__details-content[data-state="open"]{animation:frac-disclosure-open .32s cubic-bezier(.2,.7,.25,1) both}
.fractional-service__details-content[data-state="closed"]{animation:frac-disclosure-close .24s ease both}
.fractional-service__details-copy{opacity:0}
.fractional-service__details-content[data-state="open"] .fractional-service__details-copy{animation:frac-disclosure-copy-in .24s ease .06s both}
.fractional-service__details-content[data-state="closed"] .fractional-service__details-copy{animation:frac-disclosure-copy-out .12s ease both}
.fractional-service__details-copy .fractional-blocks{padding:2px 0 6px}
.fractional-service__details-copy .fractional-blocks p,.fractional-service__details-copy .fractional-blocks li{color:#5c5769}
.fractional-service__details-copy ul{padding-left:1.1rem;line-height:1.8}
.fractional-contact{position:relative;display:flex;min-height:520px;align-items:center;margin-top:105px;padding:clamp(44px,8vw,100px);isolation:isolate;background:#30203d;color:#fff}
.fractional-contact__image,.fractional-contact__overlay{position:absolute;z-index:-2;inset:0;width:100%;height:100%}
.fractional-contact__image{object-fit:cover}
.fractional-contact__overlay{z-index:-1;background:linear-gradient(90deg,rgba(25,17,31,.93) 0%,rgba(35,22,40,.79) 56%,rgba(35,22,40,.57) 100%),linear-gradient(0deg,rgba(25,17,31,.35),transparent 70%)}
.fractional-contact__content{max-width:840px}
.fractional-contact__content>.fractional-index{color:#fff}
.fractional-contact .fractional-blocks h2,.fractional-contact .fractional-blocks h3,.fractional-contact .fractional-blocks p,.fractional-contact .fractional-blocks li{color:#fff}
.fractional-contact .fractional-blocks h2{font-size:clamp(2.6rem,5.3vw,5rem);line-height:1.02}
.fractional-contact__editable{margin:20px 0 0;color:#fff;font-size:.88rem;line-height:1.6}
.fractional-contact__link{display:inline-flex;min-height:58px;align-items:center;justify-content:center;gap:22px;margin-top:27px;padding:0 24px;border:1px solid #fff;background:#fff;color:#30203d;text-decoration:none;font:700 .9rem "Space Grotesk",sans-serif;cursor:pointer;transition:transform .2s ease,background-color .2s ease,color .2s ease}
.fractional-contact__link:hover{transform:translateY(-2px);background:transparent;color:#fff}
.fractional-contact__link:focus-visible{outline:3px solid #e3aa59;outline-offset:4px}
.fractional-contact__link:disabled{cursor:not-allowed;opacity:.65}
.fractional-shell :is(.fractional-contact) :is(h1,h2,h3,h4,p,li,a,figcaption,.website-blocks,.fractional-index){color:#fff}
.fractional-inquiry[data-slot="sheet-content"]{width:min(100vw,540px)!important;max-width:none!important;gap:0;overflow-y:auto;border:0;background:#f8f4ee;padding:0;color:#292235}
.fractional-inquiry__header{padding:42px 44px 22px;border-bottom:1px solid rgba(42,32,55,.14)}
.fractional-inquiry__eyebrow{color:#925b79;font-size:.63rem;font-weight:700;letter-spacing:.16em}
.fractional-inquiry__title{margin-top:17px;color:#30203d;font-family:"DM Serif Display",Georgia,serif;font-size:clamp(2rem,5vw,2.75rem);font-weight:400;line-height:1.05}
.fractional-inquiry__description{margin-top:8px;color:#625b6a;font-size:.91rem;line-height:1.65}
.fractional-inquiry__form{display:grid;gap:16px;padding:25px 44px 34px}
.fractional-field{position:relative;display:grid;gap:7px;color:#30203d;font-size:.78rem;font-weight:700}
.fractional-field>span{display:flex;align-items:center;gap:5px}
.fractional-field>span i{color:#9b466b;font-style:normal}
.fractional-field>span small{margin-left:auto;color:#777080;font-size:.68rem;font-weight:500}
.fractional-field input,.fractional-field select,.fractional-field textarea{width:100%;min-height:44px;border:1px solid #d9cfd0;border-radius:0;background:#fffdfa;padding:10px 12px;color:#30203d;font:400 .9rem "Space Grotesk",sans-serif}
.fractional-field textarea{min-height:110px;resize:vertical}
.fractional-field :is(input,select,textarea):focus-visible{outline:2px solid #8e5577;outline-offset:1px}
.fractional-field__count{justify-self:end;color:#777080;font-size:.68rem;font-weight:400}
.fractional-inquiry__trap{position:absolute!important;left:-10000px!important;top:auto!important;width:1px!important;height:1px!important;overflow:hidden!important}
.fractional-consent{display:flex;align-items:flex-start;gap:10px;color:#5e5666;font-size:.75rem;line-height:1.55}
.fractional-consent input{width:16px;height:16px;flex:0 0 auto;margin:2px 0 0;accent-color:#57264e}
.fractional-consent a,.fractional-inquiry__error a{color:#57264e;font-weight:700;text-underline-offset:3px}
.fractional-inquiry__submit{display:flex;min-height:52px;align-items:center;justify-content:center;gap:12px;margin-top:2px;border:0;background:#57264e;color:#fff;font:700 .9rem "Space Grotesk",sans-serif;cursor:pointer}
.fractional-inquiry__submit:hover:not(:disabled){background:#40213d}
.fractional-inquiry__submit:focus-visible,.fractional-inquiry__secondary:focus-visible{outline:3px solid #d49c55;outline-offset:3px}
.fractional-inquiry__submit:disabled{cursor:wait;opacity:.75}
.fractional-inquiry__loader{animation:frac-spin 1s linear infinite}
.fractional-inquiry__pending{margin:0;color:#5e5666;font-size:.76rem;text-align:center}
.fractional-inquiry__privacy-note{display:flex;align-items:center;justify-content:center;gap:7px;margin:0;color:#746c79;font-size:.72rem}
.fractional-inquiry__error{display:grid;gap:5px;border-left:3px solid #a44455;background:#f4e7e4;padding:12px 14px;color:#49343e;font-size:.78rem;line-height:1.5}
.fractional-inquiry__success{display:grid;justify-items:start;align-content:center;min-height:65vh;gap:13px;padding:36px 44px;color:#30203d}
.fractional-inquiry__success-mark{display:grid;width:50px;height:50px;place-items:center;border-radius:50%;background:#e9dfd9;color:#57264e}
.fractional-inquiry__success h3{margin:2px 0 0;font-family:"DM Serif Display",Georgia,serif;font-size:2.6rem;font-weight:400}
.fractional-inquiry__success p:not(.fractional-inquiry__eyebrow){margin:0;color:#625b6a;line-height:1.6}
.fractional-inquiry__secondary{min-height:44px;margin-top:12px;border:1px solid #57264e;background:transparent;padding:0 20px;color:#57264e;font-weight:700;cursor:pointer}
@keyframes frac-disclosure-open{from{height:0;opacity:.65}to{height:var(--radix-collapsible-content-height);opacity:1}}
@keyframes frac-disclosure-close{from{height:var(--radix-collapsible-content-height);opacity:1}to{height:0;opacity:.65}}
@keyframes frac-disclosure-copy-in{from{opacity:0}to{opacity:1}}
@keyframes frac-disclosure-copy-out{from{opacity:1}to{opacity:0}}
@keyframes frac-spin{to{transform:rotate(360deg)}}
@keyframes frac-rise{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
@keyframes frac-shimmer{to{background-position:-200% 0}}
@media(max-width:800px){
 .fractional-steps{grid-template-columns:repeat(2,minmax(0,1fr));row-gap:20px}
 .fractional-hero__inner{width:calc(100% - 48px);padding:54px 0 68px}
 .fractional-service{grid-template-columns:minmax(0,.88fr) minmax(0,1.12fr);gap:24px;min-height:330px;padding:19px}
 .fractional-service__visual{min-height:280px}
 .fractional-service__content{padding:10px 16px 10px 0}
 .fractional-service__number{top:31px;left:32px}
 .fractional-contact{min-height:470px}
 .fractional-inquiry__header,.fractional-inquiry__form{padding-left:32px;padding-right:32px}
 .fractional-content{width:calc(100% - 48px);padding-top:86px}
 .fractional-section-heading,.fractional-delivery__intro{grid-template-columns:130px 1fr}
 .fractional-how{gap:35px}
 .fractional-how__visual{min-height:340px}
 .fractional-step{padding-right:14px}
 .fractional-step:not(:last-child):after{right:12px}
}
@media(max-width:640px){
 .fractional-page{padding-top:72px}
 .fractional-hero{min-height:0}
 .fractional-hero__inner{display:block;min-height:0;width:calc(100% - 40px);padding:48px 0 63px}
 .fractional-hero__copy{width:100%;max-width:100%}
 .fractional-hero h1{margin:25px 0 17px;font-size:clamp(3rem,14vw,4.5rem)}
 .fractional-hero__summary{font-size:1.06rem}
 .fractional-hero__side-note{display:none}
 .fractional-hero__bottomline{left:20px;right:20px}
 .fractional-content{width:calc(100% - 40px);padding:75px 0 60px}
 .fractional-services{padding-bottom:78px}
 .fractional-section-heading{grid-template-columns:1fr;gap:15px;margin-bottom:25px}
 .fractional-section-heading .fractional-index{padding-top:0}
 .fractional-service{grid-template-columns:minmax(0,1fr);gap:16px;min-height:0;margin-bottom:4px;padding:14px;border-radius:14px}
 .fractional-service__visual{grid-column:1;grid-row:1;min-height:0;aspect-ratio:16/9;border-radius:9px}
 .fractional-service__content{grid-column:1;grid-row:2;padding:6px 8px 22px}
 .fractional-service__number{top:27px;left:27px}
 .fractional-service__number{font-size:1.17rem}
 .fractional-service .fractional-blocks h2{font-size:1.7rem}
 .fractional-how{grid-template-columns:1fr;gap:29px;padding:63px 0 76px}
 .fractional-how__visual{min-height:300px}
 .fractional-delivery{margin:0 -20px;padding:65px 20px 70px}
 .fractional-delivery__intro{grid-template-columns:1fr;gap:16px;margin-bottom:34px}
 .fractional-steps{grid-template-columns:1fr 1fr;row-gap:20px}
 .fractional-step{padding:20px 14px 6px 0}
 .fractional-step:not(:last-child):after{display:none}
 .fractional-contact{min-height:520px;margin-top:65px;padding:38px 23px}
 .fractional-contact .fractional-blocks h2{font-size:2.45rem}
 .fractional-contact__overlay{background:linear-gradient(90deg,rgba(25,17,31,.94),rgba(35,22,40,.75))}
 .fractional-inquiry[data-slot="sheet-content"]{width:100%!important;padding:0}
 .fractional-inquiry__header{padding:34px 23px 19px}
 .fractional-inquiry__form{gap:14px;padding:20px 23px 28px}
 .fractional-inquiry__success{padding:28px 23px}
}
@media(prefers-reduced-motion:reduce){
 .fractional-shell *, .fractional-shell *:before, .fractional-shell *:after{scroll-behavior:auto!important;animation-duration:.01ms!important;animation-iteration-count:1!important;transition-duration:.01ms!important}
  .fractional-service__details-content[data-state],.fractional-service__details-copy{animation:none!important}
  .fractional-service__details-copy{opacity:1!important}
  .fractional-service__details-chevron{transition:none!important}
}
`;
